import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Slide,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@material-ui/core';
import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IOpportunityItem } from '../../../models/opportunityItem.model';
import { PageFilter } from '../../../state/app';
import { AppState } from '../../../state/configureStore';
import {
  clearOpportunityItem,
  fetchOpportunityItemList,
  OpportunityItemListFilter,
  OpportunityItemState,
} from '../../../state/opportunityItem';
import { TaskState } from '../../../state/task';
import { selectedTaskItems, TaskItemState } from '../../../state/taskItem';
import { SearchField, SortLabel, TableLoading } from '..';
import { useTableSorting } from '../../hooks';
import useStyles from './taskItemChooseDialog.style';
import { TransitionProps } from '@material-ui/core/transitions';
import { Close as CloseIcon, Done as DoneIcon } from '@material-ui/icons';
import { TablePageSize } from '../../../constants';
import { Grid as MUIGrid } from '@mui/material';

interface TaskItemChooseDialogProps {
  open: boolean;
  handleClose: () => void;
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return (
    <Slide direction="up" ref={ref} {...props} timeout={{ enter: 400, exit: 400 }} />
  );
});

const TaskItemChooseDialog: React.FC<TaskItemChooseDialogProps> = (props) => {
  const { open, handleClose } = props;
  const dispatch = useDispatch();
  const classes = useStyles();
  const [sort, setSort] = useTableSorting(
    {
      orderBy: 'Opportunity.AssignmentNumber',
      direction: 'desc',
    },
    true,
  );
  const [filters, setFilters] = useState<OpportunityItemListFilter>({});
  const [paging, setPaging] = useState<PageFilter>({
    pageNumber: 0,
    pageSize: 10,
  });
  const { selectedItems } = useSelector<AppState, TaskItemState>(
    (state) => state.taskItem,
  );
  const initSelectedOpportunityItems = useMemo(
    () =>
      selectedItems.flatMap((selectedItem) => {
        return selectedItem.opportunityItem ? [selectedItem.opportunityItem] : [];
      }),
    [selectedItems],
  );
  const [selectedOpportunityItems, setSelectedOpportunityItems] = useState<
    IOpportunityItem[]
  >(initSelectedOpportunityItems);
  const { items: opportunityItems, totalItems, loading } = useSelector<
    AppState,
    OpportunityItemState
  >((state) => state.opportunityItem);

  const {
    item: { id: taskId },
  } = useSelector<AppState, TaskState>((state) => state.task);

  useEffect(() => {
    const newPaging = { ...paging, sort };
    dispatch(fetchOpportunityItemList(filters, newPaging));
    return function cleanUp() {
      dispatch(clearOpportunityItem());
    };
  }, [dispatch, paging, filters, sort]);

  const handleChangeFilter = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const { target } = event;
    setFilters((prevState) => ({
      ...prevState,
      [target.name]: target.value,
    }));
    setPaging({
      ...paging,
      pageNumber: 0,
    });
  };

  const handleChangePageNumber = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => {
    setPaging({ ...paging, pageNumber: newPage });
  };

  const handleChangePageSize = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setPaging({
      ...paging,
      pageSize: +event.target.value,
      pageNumber: 0,
    });
  };

  return (
    <Dialog
      classes={{ paper: classes.dialog }}
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth={true}
      // disableBackdropClick={true}
      TransitionComponent={Transition}
    >
      <DialogTitle className={classes.dialogTitle} disableTypography>
        <Box className={classes.titleContent}>
          <Box className={classes.titleText}>
            <Typography variant="h4" className={classes.titleHeading}>
              Select Assignment Item
            </Typography>
            <Typography variant="body1" className={classes.titleSubtitle}>
              Filter, review, and select the assignment items.
            </Typography>
          </Box>
          <Box className={classes.titleActions}>
            <Box className={classes.selectionBadge}>
              {selectedOpportunityItems.length} selected
            </Box>
            <IconButton
              aria-label="close"
              onClick={() => {
                handleClose();
              }}
              className={classes.closeButton}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent className={classes.content}>
        <Box className={classes.filterSurface}>
          <Box className={classes.filterHeader}>
            <Typography variant="subtitle1" className={classes.surfaceTitle}>
              Filter assignments
            </Typography>
          </Box>
          <MUIGrid container spacing={1}>
            <MUIGrid xs={3}>
              <SearchField
                label="Assignment Number"
                onChange={handleChangeFilter}
                filterKey="Opportunity.AssignmentNumber.startsWith"
                filters={filters}
                fullWidth
              />
            </MUIGrid>
            <MUIGrid xs={3}>
              <SearchField
                label="Program"
                onChange={handleChangeFilter}
                filterKey="Opportunity.Program.Name.startsWith"
                filters={filters}
                fullWidth
              />
            </MUIGrid>
            <MUIGrid xs={3}>
              <SearchField
                label="Block No"
                onChange={handleChangeFilter}
                filterKey="Block.BlockNo.startsWith"
                filters={filters}
                fullWidth
              />
            </MUIGrid>
            <MUIGrid xs={3}>
              <SearchField
                label="Item"
                onChange={handleChangeFilter}
                filterKey="Block.Item.startsWith"
                filters={filters}
                fullWidth
              />
            </MUIGrid>
          </MUIGrid>
        </Box>

        <Box className={classes.tableSurface}>
          <Table style={{ marginTop: 0 }}>
            <TableHead>
              <TableRow className={classes.tableHeadRow}>
                <TableCell padding="checkbox" className={classes.tableHeadCell}>
                  <Checkbox
                    onChange={(event: React.ChangeEvent<HTMLInputElement>, checked) => {
                      if (checked) {
                        const isItemAdded = (opItem: IOpportunityItem) => {
                          return (
                            selectedOpportunityItems.findIndex(
                              (selectedOpportunityItem) =>
                                selectedOpportunityItem.id === opItem.id,
                            ) === -1
                          );
                        };
                        const itemToAdd = opportunityItems.filter(isItemAdded);
                        setSelectedOpportunityItems([
                          ...selectedOpportunityItems,
                          ...itemToAdd,
                        ]);
                      } else {
                        setSelectedOpportunityItems([]);
                      }
                    }}
                  />
                </TableCell>
                <TableCell className={classes.tableHeadCell}>No.</TableCell>
                <TableCell className={classes.tableHeadCell}>
                  <SortLabel
                    sort={sort}
                    handleSortChange={setSort}
                    field="Opportunity.AssignmentNumber"
                  >
                    Assignment Number
                  </SortLabel>
                </TableCell>
                <TableCell className={classes.tableHeadCell}>Company</TableCell>
                <TableCell className={classes.tableHeadCell}>Program</TableCell>
                <TableCell className={classes.tableHeadCell}>
                  <SortLabel sort={sort} handleSortChange={setSort} field="Block.BlockNo">
                    Block No
                  </SortLabel>
                </TableCell>
                <TableCell className={classes.tableHeadCell}>Item</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableLoading isLoading={loading} colSpan={10}>
                {opportunityItems.map((opportunityItem, index) => {
                  const { id } = opportunityItem;
                  const isCheckboxChecked =
                    selectedOpportunityItems.findIndex(
                      (selectedOpportunityItem) => selectedOpportunityItem.id === id,
                    ) !== -1;
                  return (
                    <TableRow key={opportunityItem.id} className={classes.resultRow}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          onChange={(
                            event: React.ChangeEvent<HTMLInputElement>,
                            checked,
                          ) => {
                            if (checked) {
                              setSelectedOpportunityItems([
                                ...selectedOpportunityItems,
                                opportunityItem,
                              ]);
                            } else {
                              const newSelectedOpportunityItems = [
                                ...selectedOpportunityItems,
                              ];
                              const selectedIndex = selectedOpportunityItems.findIndex(
                                (selectedOpportunityItem) =>
                                  selectedOpportunityItem.id === id,
                              );
                              newSelectedOpportunityItems.splice(selectedIndex, 1);
                              setSelectedOpportunityItems(newSelectedOpportunityItems);
                            }
                          }}
                          checked={isCheckboxChecked}
                        />
                      </TableCell>
                      <TableCell>
                        {index + paging.pageSize * paging.pageNumber + 1}
                      </TableCell>
                      <TableCell>
                        {opportunityItem.opportunity?.assignmentNumber}
                      </TableCell>
                      <TableCell>{opportunityItem.opportunity?.company?.name}</TableCell>
                      <TableCell>{opportunityItem.block?.program?.name}</TableCell>
                      <TableCell>{opportunityItem.block?.blockNo}</TableCell>
                      <TableCell>{opportunityItem.block?.item}</TableCell>
                    </TableRow>
                  );
                })}
              </TableLoading>
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[...TablePageSize]}
            component="div"
            count={totalItems}
            rowsPerPage={paging.pageSize}
            page={paging.pageNumber}
            onPageChange={handleChangePageNumber}
            onRowsPerPageChange={handleChangePageSize}
            className={classes.pagination}
          />
        </Box>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <Typography className={classes.actionText}>
          Confirm the selected assignment items to bring them back into the task creation
          flow.
        </Typography>
        <Box>
          <Button
            onClick={handleClose}
            variant="outlined"
            color="primary"
            startIcon={<CloseIcon />}
            className={classes.secondaryButton}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<DoneIcon />}
            onClick={() => {
              const taskItems = selectedOpportunityItems.map(
                (selectedOpportunityItem) => ({
                  smTaskId: taskId,
                  opportunityItemId: selectedOpportunityItem.id,
                  opportunityItem: selectedOpportunityItem,
                }),
              );
              dispatch(selectedTaskItems(taskItems));
              handleClose();
            }}
            color="primary"
            className={classes.primaryButton}
          >
            Confirm
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default TaskItemChooseDialog;
